/** This file contains the components necessary for rendering Thoughtspot */
import { captureEvent, captureException } from '@sentry/react';
import {
  AppEmbed,
  type AppViewConfig,
  EmbedEvent,
  HomepageModule,
  HomePageSearchBarMode,
} from '@thoughtspot/visual-embed-sdk';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useUserAppViewConfig } from '@/api/queries/useSdkTokenQuery';
import { useFlag, useFlagsStatus } from '@alchemer/unleash';
import { FEATURE_FLAGS } from '@/config/features';
import { tw } from '@/lib/tailwind';
import { injectWalkMe } from '@/lib/walkme';
import { useBoundStore } from '@/store';
import { ThoughtSpotSdkAuthStatus } from '@/store/slices/createThoughtspotSlice';
import type {
  PreRenderedAppEmbedContextType,
  ShareDialogPayload,
  ThoughtSpotCoords,
  ThoughtSpotEmbedAction,
  ThoughtSpotUnderlyingFrame,
} from '@/types';
import { ShareDialog } from './ShareDialog/ShareDialog';

// Context
const DEFAULT_PRERENDERED_EMBED_CONTEXT: PreRenderedAppEmbedContextType = {
  isVisible: false,
  setIsVisible: () => undefined,
  setPath: () => undefined,
  coords: { left: 0, top: 0, height: 0, width: 0 },
  setCoords: () => undefined,
  embedRef: { current: null },
  isAuthenticated: false,
  handleNavigation: () => undefined,
  isReady: false,
  setIsReady: () => undefined,
  initializeView: () => undefined,
  isShareDialogOpen: false,
  sharePayload: null,
  openShareDialog: () => undefined,
  closeShareDialog: () => undefined,
};

export const PrerenderedAppEmbedContext = createContext<PreRenderedAppEmbedContextType>(
  DEFAULT_PRERENDERED_EMBED_CONTEXT,
);

// Constants
const THOUGHTSPOT_CONFIG: AppViewConfig = {
  liveboardV2: true,
  enableAskSage: true,
  modularHomeExperience: true,
  dataPanelV2: true,
  hiddenHomepageModules: Array.from([HomepageModule.Learning]),
  isUnifiedSearchExperienceEnabled: false,
  homePageSearchBarMode: HomePageSearchBarMode.AI_ANSWER,
  enableCustomColumnGroups: true,
  isLiveboardMasterpiecesEnabled: true,
  additionalFlags: { enableCustomColumnGroups: true },
};

const INITIAL_COORDS: ThoughtSpotCoords = {
  left: 0,
  top: 0,
  height: 0,
  width: 0,
};

const COMPONENT_NAME = 'PrerenderedThoughtSpotShell';

/**
 * @description Utility function to get the offset of an HTML element
 */
export function getOffset(el: HTMLElement) {
  let _x = 0;
  let _y = 0;
  while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
    _x += el.offsetLeft - el.scrollLeft;
    _y += el.offsetTop - el.scrollTop;
    el = el.offsetParent as HTMLElement;
  }
  return { top: _y, left: _x };
}

/**
 * @description Utility function to report errors to Sentry with context
 */
function reportThoughtSpotEmbedError(error: unknown, action: ThoughtSpotEmbedAction) {
  captureException(error, {
    level: 'error',
    extra: { component: COMPONENT_NAME, action },
  });
}

export function PrerenderedThoughtSpotShell() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isVisible, coords, embedRef, isAuthenticated, setIsReady, openShareDialog } =
    useContext(PrerenderedAppEmbedContext);
  const { authStatus } = useBoundStore();
  const userAppViewConfig = useUserAppViewConfig();
  const customShareEnabled = useFlag(FEATURE_FLAGS.CUSTOM_SHARE);
  const { flagsReady } = useFlagsStatus();
  const hasCleanedUpRef = useRef(false);

  /**
   * Cleans up the ThoughtSpot embed instance and removes its iframe from the DOM
   *
   * @description This function ensures that the ThoughtSpot embed instance is properly
   * destroyed and its associated iframe is removed from the DOM to prevent memory leaks.
   * It handles cases where the embed instance or iframe may not exist, logging errors
   * to Sentry as needed.
   */
  function safeDestroy(embed: AppEmbed) {
    try {
      embed.destroy?.();
    } catch (error) {
      reportThoughtSpotEmbedError(error, 'cleanup-destroy');
    }
  }

  /**
   * Removes the ThoughtSpot iframe from the DOM
   *
   * @description Safely removes the iframe element from the DOM
   */
  function safeRemoveFrame(frame: ThoughtSpotUnderlyingFrame) {
    const parent = frame.parentNode;
    if (!parent) return;

    // Only remove if the parent supports removeChild and the frame is still connected
    if ('removeChild' in parent && frame.isConnected !== false) {
      try {
        parent.removeChild(frame as unknown as Node);
      } catch (error) {
        reportThoughtSpotEmbedError(error, 'cleanup-remove-frame');
      }
    }
  }

  const cleanupEmbed = useCallback((): boolean => {
    // Prevent multiple cleanups
    if (hasCleanedUpRef.current) return false;

    const embed = embedRef.current;
    if (!embed) return false;
    embedRef.current = null;

    // Attempt to destroy the embed and remove its iframe
    try {
      const frame = embed.getUnderlyingFrameElement?.() as ThoughtSpotUnderlyingFrame | undefined;
      // Destroy the embed instance
      safeDestroy(embed);

      // Remove the iframe from the DOM
      if (!frame) return true; // expected when not yet fully initialized
      safeRemoveFrame(frame);

      return true;
    } catch (error) {
      reportThoughtSpotEmbedError(error, 'cleanup');
      return true;
    } finally {
      // Mark as cleaned up
      hasCleanedUpRef.current = true;
    }
  }, [embedRef]);

  /**
   * Sets up WalkMe integration with the ThoughtSpot embed
   *
   * @description This function injects the WalkMe script into the ThoughtSpot iframe
   * once the iframe is fully loaded. It handles both the case where
   * the iframe is already loaded and the case where it loads later.
   */
  const setupWalkMe = useCallback((embed: AppEmbed) => {
    const frame = embed.getUnderlyingFrameElement?.() as HTMLIFrameElement | undefined;
    if (!frame) return;

    const inject = () => {
      try {
        injectWalkMe(frame);
      } catch (error) {
        reportThoughtSpotEmbedError(error, 'walkmeInjection');
      }
    };

    const ready = frame.contentWindow?.document?.readyState === 'complete';
    if (ready) {
      inject();
    } else if (frame.contentWindow?.addEventListener) {
      frame.contentWindow.addEventListener(
        'load',
        () => {
          try {
            injectWalkMe(frame);
          } catch (error) {
            reportThoughtSpotEmbedError(error, 'walkmeInjectionOnLoad');
          }
        },
        { once: true },
      );
    } else {
      captureEvent({
        message: 'Unable to set up WalkMe injection; iframe contentWindow is not ready.',
        level: 'warning',
        extra: { source: 'walkme_injection_iframe_not_ready' },
      });
    }
  }, []);

  /**
   * Effect to initialize the ThoughtSpot AppEmbed
   *
   * @description This effect runs when the component mounts and when the `isAuthenticated`
   * or `userAppViewConfig` values change. It initializes the ThoughtSpot AppEmbed
   * with the provided configuration and sets up WalkMe integration.
   * The effect also cleans up the embed instance when the component unmounts
   * or when dependencies change.
   *
   * The embed is created once when:
   * - User is authenticated (isAuthenticated = true)
   * - SDK initialization has completed (authStatus = 'authenticated')
   * - Container ref exists
   * - Unleash flags have loaded (flag-driven config would otherwise re-create the embed)
   *
   * Token refreshes (every 20 minutes) happen automatically in the background without
   * re-creating the embed, preserving all user state throughout the session.
   */
  useEffect(() => {
    if (!isAuthenticated) return; // Do not instantiate embed until authenticated
    if (!flagsReady) return;
    if (!containerRef.current) return;

    if (authStatus !== ThoughtSpotSdkAuthStatus.Authenticated) {
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        // Set the ThoughtSpot config based on the users persisted settings
        const finalConfig = { ...THOUGHTSPOT_CONFIG, ...userAppViewConfig };

        // Initialize the ThoughtSpot AppEmbed
        const containerSelector = containerRef.current as unknown as string;
        embedRef.current = new AppEmbed(containerSelector, finalConfig);

        if (customShareEnabled) {
          embedRef.current.on(EmbedEvent.CustomAction, (payload) => {
            if (payload.data?.id?.startsWith('share-custom-action')) {
              openShareDialog(payload.data as unknown as ShareDialogPayload);
            }
          });
        }

        // Pre-render the embed to improve perceived performance
        await embedRef.current.prerenderGeneric();
        if (!isMounted || !embedRef.current) return;

        // Inject WalkMe into the ThoughtSpot iframe
        setupWalkMe(embedRef.current);

        // Notify that the embed is ready
        setIsReady(true);
      } catch (error) {
        reportThoughtSpotEmbedError(error, 'initializeEmbed');
      }
    })();

    // Cleanup function to destroy the embed on unmount or dependency change
    return () => {
      isMounted = false;
      cleanupEmbed();
    };
  }, [
    isAuthenticated,
    flagsReady,
    authStatus,
    setupWalkMe,
    cleanupEmbed,
    userAppViewConfig,
    embedRef,
    setIsReady,
    openShareDialog,
    customShareEnabled,
  ]);

  const containerClasses = tw(
    'absolute',
    coords.left && `left-[${coords.left}px]`,
    coords.top && `top-[${coords.top}px]`,
    'w-full',
    'h-[calc(100vh-var(--dashboard-menu-height))]',
    isVisible ? 'block -z-1' : 'hidden',
  );

  const containerStyle = {
    left: 'var(--sidebar-content-offset, 0px)',
    width: 'calc(100vw - var(--sidebar-content-offset, 0px))',
  };

  return (
    <div
      id="prerender"
      data-testid="prerender"
      className={containerClasses}
      style={containerStyle}
      ref={containerRef}
      aria-hidden={!isVisible}
    />
  );
}

export function PrerenderedThoughtSpotProvider({
  children,
  isAuthenticated,
}: PropsWithChildren & { isAuthenticated: boolean }) {
  const customShareEnabled = useFlag(FEATURE_FLAGS.CUSTOM_SHARE);
  const [isVisible, setIsVisible] = useState(false);
  const [path, setPath] = useState<string>();
  const [coords, setCoords] = useState<ThoughtSpotCoords>(INITIAL_COORDS);
  const embedRef = useRef<AppEmbed | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState<ShareDialogPayload | null>(null);

  const openShareDialog = useCallback((payload: ShareDialogPayload) => {
    setSharePayload(payload);
    setIsShareDialogOpen(true);
  }, []);

  const closeShareDialog = useCallback(() => {
    setIsShareDialogOpen(false);
    setSharePayload(null);
  }, []);

  /**
   * Handles navigation within the ThoughtSpot embed
   */
  const handleNavigation = useCallback(
    (targetPath: string) => {
      if (!embedRef.current || !isReady) return;
      try {
        embedRef.current.navigateToPage(targetPath);
      } catch (error) {
        reportThoughtSpotEmbedError(error, 'navigation');
      }
    },
    [isReady],
  );

  /**
   * Initialize view with path, coords, and visibility in a single update
   */
  const initializeView = useCallback(
    (targetPath: string, targetCoords: ThoughtSpotCoords) => {
      setPath(targetPath);
      setCoords(targetCoords);
      setIsVisible(true);
      handleNavigation(targetPath);
    },
    [handleNavigation],
  );

  const contextValue = useMemo(
    () => ({
      isVisible,
      setIsVisible,
      path,
      setPath,
      coords,
      setCoords,
      embedRef,
      isAuthenticated,
      handleNavigation,
      isReady,
      setIsReady,
      initializeView,
      isShareDialogOpen,
      sharePayload,
      openShareDialog,
      closeShareDialog,
    }),
    [
      isVisible,
      path,
      coords,
      isAuthenticated,
      isReady,
      handleNavigation,
      initializeView,
      isShareDialogOpen,
      sharePayload,
      openShareDialog,
      closeShareDialog,
    ],
  );

  return (
    <PrerenderedAppEmbedContext.Provider value={contextValue}>
      {children}
      <PrerenderedThoughtSpotShell />
      {customShareEnabled && (
        <ShareDialog
          open={isShareDialogOpen}
          payload={sharePayload}
          onClose={closeShareDialog}
        />
      )}
    </PrerenderedAppEmbedContext.Provider>
  );
}
